import http from 'apis/config/http-service';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { useQuery, UseQueryResult } from 'react-query';
import type { ProductBrand } from './types';
import { productBrandsEndpoint } from './use-get-product-brands-api';

// GET api/v1/cms/product-brands?isActive=true — active brand names for the
// master-product Brand dropdowns (variant modal, bulk variants grid, inline
// variant form). Modeled on use-get-uom-options-api, but the dictionary is
// DB-managed and can change mid-session, so staleTime is 5 minutes instead of
// Infinity. Retries stay at the React Query default: brand is REQUIRED on the
// variant form, so an empty list blocks creation rather than merely degrading.
//
// Legacy values not in the list stay editable through buildCanonicalOptions'
// "(legacy)" option — see canonical-options.ts.

// Stable fallback: `data ?? []` would allocate a new array every render, and
// these options feed useMemo/useEffect dependency arrays.
export const EMPTY_BRAND_OPTIONS: readonly string[] = [];

export const useGetBrandOptionsApi = (): UseQueryResult<string[], unknown> => {
  const isLoggedIn = useIsLoggedIn();
  return useQuery({
    queryKey: [productBrandsEndpoint, { isActive: true }],
    queryFn: async (): Promise<string[]> => {
      const { data } = await http.get(productBrandsEndpoint, { params: { isActive: true } });
      return (data?.data ?? []).map((brand: ProductBrand) => brand.name);
    },
    staleTime: 300_000,
    enabled: isLoggedIn,
  });
};
